import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Image, Spinner, Alert } from 'react-bootstrap';
import { BASE_URL, UPLOAD_BASE_URL } from '../../services/api';

const History = () => {
  const [historyData, setHistoryData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    fetchHistoryData();
    // Trigger animations after component mounts
    setTimeout(() => setIsVisible(true), 100);
  }, []);

  const fetchHistoryData = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await fetch(`${BASE_URL}/public/about.php?action=getAll`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const result = await response.json();
      
      if (result.success && result.data) {
        setHistoryData(result.data);
      } else {
        setError(result.message || 'Failed to fetch history data');
      }
    } catch (error) {
      console.error('Fetch error:', error);
      setError('Error loading history: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const getLogoUrl = () => {
    if (!historyData) return 'https://via.placeholder.com/300x150?text=College+Logo';
    
    if (historyData.logo_url) return historyData.logo_url;
    
    if (historyData.logo) {
      return getImageUrl(historyData.logo);
    }
    
    return 'https://via.placeholder.com/300x150?text=College+Logo';
  };

  const getImageUrl = (imagePath) => {
    if (!imagePath) return 'https://via.placeholder.com/500x300?text=College+Logo';
    
    if (imagePath.startsWith('http')) return imagePath;
    
    if (imagePath.startsWith('uploads/')) {
      return `${UPLOAD_BASE_URL}/${imagePath}`;
    }
    
    return `${UPLOAD_BASE_URL}/${imagePath}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (e) {
      return dateString;
    }
  };

  const getHistoryParagraphs = () => {
    if (!historyData || !historyData.history) return [];
    return historyData.history.split('\n\n').filter(p => p.trim() !== '');
  };

  if (loading) {
    return (
      <>
        <div style={{ height: '0px' }}></div>
        <Container className="py-5 text-center">
          <div className="loading-wrapper">
            <Spinner animation="border" variant="primary" className="loading-spinner" />
            <p className="mt-3 loading-text">Loading our journey through time...</p>
          </div>
        </Container>
      </>
    );
  }

  if (error) {
    return (
      <>
        <div style={{ height: '140px' }}></div>
        <Container className="py-5">
          <Alert variant="danger" className="animated-alert">
            <i className="fas fa-exclamation-triangle me-2"></i>
            {error}
            <div className="mt-2">
              <button 
                className="btn btn-sm btn-outline-primary"
                onClick={fetchHistoryData}
              >
                <i className="fas fa-redo me-1"></i>
                Try Again
              </button>
            </div>
          </Alert>
        </Container>
      </>
    );
  }

  const historyParagraphs = getHistoryParagraphs();

  return (
    <>
      {/* Navbar Spacer */}
      <div style={{ height: '140px' }}></div>
      
      {/* Animated Background Elements */}
      <div className="animated-background">
        <div className="bg-shape shape-1"></div>
        <div className="bg-shape shape-2"></div>
        <div className="bg-shape shape-3"></div>
        <div className="bg-shape shape-4"></div>
      </div>
      
      <Container className="py-5 position-relative">
        {/* Header with Logo Left and Content Right */}
        <Row className="mb-5 align-items-center">
          {/* Left Column - Logo */}
          <Col lg={4} md={5} className="mb-4 mb-md-0">
            <div className={`logo-container ${isVisible ? 'animate-logo' : ''}`}>
              <div className="logo-wrapper">
                <div className="logo-orbital-ring"></div>
                <div className="logo-glow-effect"></div>
                <Image 
                  src={getLogoUrl()}
                  alt="College Logo" 
                  fluid
                  className="logo-image"
                />
              </div>
            </div>
          </Col>
          
          {/* Right Column - Title and Description */}
          <Col lg={8} md={7}>
            <div className={`header-content ${isVisible ? 'fade-in-up' : ''}`}>
              {/* Badge/Chip */}
              <div className="badge-wrapper mb-3">
                <span className="history-badge">
                  <i className="fas fa-history me-2"></i>
                  Our Legacy Timeline
                </span>
              </div>
              
              {/* Main Title */}
              <h1 className="main-title">
                <span className="title-text">Journey Through</span>
                <span className="title-highlight"> Excellence</span>
              </h1>
              
              {/* Subtitle/Description
              <div className="description-box">
                <div className="description-icon">
                  <i className="fas fa-quote-left"></i>
                </div>
                <p className="description-text">
                  {historyData?.title || 'Discover the inspiring journey of our institution from its humble beginnings to becoming a beacon of knowledge and excellence in education.'}
                </p>
                <div className="description-footer">
                  <span className="est-year">
                    <i className="fas fa-calendar-star me-2"></i>
                    Established: <strong>{formatDate(historyData?.established_date) || 'Since Beginning'}</strong>
                  </span>
                </div>
              </div>
               */}
              {/* Timeline Indicator */}
              <div className="timeline-indicator mt-4">
                <div className="indicator-track">
                  <div className="track-line"></div>
                  <div className="track-marker start">
                    <i className="fas fa-flag-checkered"></i>
                    <span className="marker-label">Foundation</span>
                  </div>
                  <div className="track-marker middle">
                    <i className="fas fa-chart-line"></i>
                    <span className="marker-label">Growth</span>
                  </div>
                  <div className="track-marker end">
                    <i className="fas fa-trophy"></i>
                    <span className="marker-label">Excellence</span>
                  </div>
                </div>
              </div>
            </div>
          </Col>
        </Row>

        {/* History Content Card */}
        <Row className="justify-content-center">
          <Col lg={12}>
            <div className={`history-card-wrapper ${isVisible ? 'slide-in' : ''}`}>
              <Card className="animated-card">
                {/* Card Header with Decorative Elements */}
                <div className="card-header-decorative">
                  <div className="decorative-wave">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 320">
                      <path fill="#0d6efd" fillOpacity="0.1" d="M0,96L48,112C96,128,192,160,288,160C384,160,480,128,576,112C672,96,768,96,864,112C960,128,1056,160,1152,160C1248,160,1344,128,1392,112L1440,96L1440,0L1392,0C1344,0,1248,0,1152,0C1056,0,960,0,864,0C768,0,672,0,576,0C480,0,384,0,288,0C192,0,96,0,48,0L0,0Z"></path>
                    </svg>
                  </div>
                </div>
                
                <Card.Body className="p-4 p-md-5">
                  {/* Section Title */}
                  <div className="section-title-wrapper mb-5">
                    <h2 className="section-title">
                      <i className="fas fa-book-open me-3"></i>
                      Our Historical Journey
                    </h2>
                    <p className="section-subtitle">
                      A comprehensive narrative of our institution's evolution and achievements
                    </p>
                    <div className="title-divider">
                      <span className="divider-line"></span>
                      <span className="divider-icon">
                        <i className="fas fa-star"></i>
                      </span>
                      <span className="divider-line"></span>
                    </div>
                  </div>
                  
                  {historyParagraphs.length > 0 ? (
                    <div className="history-content">
                      {/* Animated Decorative Elements */}
                      <div className="content-decoration">
                        <div className="decoration-circle circle-1"></div>
                        <div className="decoration-circle circle-2"></div>
                        <div className="decoration-line"></div>
                      </div>
                      
                      {historyParagraphs.map((paragraph, index) => (
                        <div 
                          key={index} 
                          className={`history-paragraph ${isVisible ? 'paragraph-enter' : ''}`}
                          style={{ animationDelay: `${index * 0.2}s` }}
                        >
                          {/* Timeline Node */}
                          <div className="timeline-node">
                            <div className="node-circle">
                              <span className="node-number">{index + 1}</span>
                            </div>
                            <div className="node-connector"></div>
                          </div>
                          
                          {/* Paragraph Content */}
                          <div className="paragraph-content">
                            <div className="paragraph-card">
                              <div className="card-header">
                                <h3 className="paragraph-title">
                                  <i className="fas fa-landmark me-2"></i>
                                  Chapter {index + 1}: {getParagraphTitle(index)}
                                </h3>
                                <span className="chapter-badge">
                                  <i className="fas fa-clock me-1"></i>
                                  {getChapterPeriod(index)}
                                </span>
                              </div>
                              <div className="card-body">
                                {index === 0 ? (
                                  <p className="paragraph-text">
                                    <span className="drop-cap">{paragraph.charAt(0)}</span>
                                    {paragraph.slice(1)}
                                  </p>
                                ) : (
                                  <p className="paragraph-text">{paragraph}</p>
                                )}
                              </div>
                              <div className="card-footer">
                                <div className="footer-icons">
                                  <span className="icon-item">
                                    <i className="fas fa-calendar"></i>
                                    <span>Milestone</span>
                                  </span>
                                  <span className="icon-item">
                                    <i className="fas fa-users"></i>
                                    <span>Community</span>
                                  </span>
                                  <span className="icon-item">
                                    <i className="fas fa-graduation-cap"></i>
                                    <span>Education</span>
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <Alert variant="info" className="animated-alert">
                      <i className="fas fa-info-circle me-2"></i>
                      History content is being updated. Please check back soon.
                    </Alert>
                  )}
                </Card.Body>
                
                {/* Card Footer with Statistics */}
                <Card.Footer className="history-footer">
                  <div className="footer-stats">
                    <div className="stat-item">
                      <div className="stat-icon">
                        <i className="fas fa-calendar-alt"></i>
                      </div>
                      <div className="stat-content">
                        <h4>Years of Excellence</h4>
                        <p>Since {formatDate(historyData?.established_date)?.split(',')[1] || 'Beginning'}</p>
                      </div>
                    </div>
                    <div className="stat-item">
                      <div className="stat-icon">
                        <i className="fas fa-graduation-cap"></i>
                      </div>
                      <div className="stat-content">
                        <h4>Educational Legacy</h4>
                        <p>Quality Education Provider</p>
                      </div>
                    </div>
                    <div className="stat-item">
                      <div className="stat-icon">
                        <i className="fas fa-trophy"></i>
                      </div>
                      <div className="stat-content">
                        <h4>Continuous Growth</h4>
                        <p>Always Evolving & Improving</p>
                      </div>
                    </div>
                  </div>
                </Card.Footer>
              </Card>
            </div>
          </Col>
        </Row>

        {/* Floating Historical Elements */}
        <div className="floating-elements">
          <div className="floating-element element-1">
            <i className="fas fa-book-open"></i>
          </div>
          <div className="floating-element element-2">
            <i className="fas fa-history"></i>
          </div>
          <div className="floating-element element-3">
            <i className="fas fa-landmark"></i>
          </div>
        </div>
      </Container>

      {/* Advanced CSS Animations and Styles */}
      <style jsx="true">{`
        /* Base Styles */
        .history-content {
          line-height: 1.8;
        }
        
        /* Loading Animation */
        .loading-wrapper {
          position: relative;
          min-height: 300px;
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
        }
        
        .loading-spinner {
          width: 3rem;
          height: 3rem;
          animation: rotate 1.5s linear infinite;
        }
        
        .loading-text {
          font-size: 1.2rem;
          background: linear-gradient(45deg, #0d6efd, #198754);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          animation: colorShift 3s infinite alternate;
        }
        
        /* Animated Background */
        .animated-background {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          z-index: -1;
          overflow: hidden;
        }
        
        .bg-shape {
          position: absolute;
          border-radius: 50%;
          background: linear-gradient(45deg, rgba(13, 110, 253, 0.1), rgba(25, 135, 84, 0.05));
          filter: blur(40px);
        }
        
        .shape-1 {
          width: 300px;
          height: 300px;
          top: 10%;
          left: 5%;
          animation: float 20s infinite alternate ease-in-out;
        }
        
        .shape-2 {
          width: 200px;
          height: 200px;
          top: 60%;
          right: 10%;
          animation: float 25s infinite alternate-reverse ease-in-out;
        }
        
        .shape-3 {
          width: 150px;
          height: 150px;
          bottom: 20%;
          left: 15%;
          animation: float 30s infinite alternate ease-in-out;
        }
        
        .shape-4 {
          width: 250px;
          height: 250px;
          top: 30%;
          right: 5%;
          animation: float 35s infinite alternate-reverse ease-in-out;
        }
        
        /* Logo Container - Left Side */
        .logo-container {
          position: relative;
          padding: 20px;
        }
        
        .logo-wrapper {
          position: relative;
          display: flex;
          justify-content: center;
          align-items: center;
          background: linear-gradient(135deg, #ffffff, #f8f9fa);
          border-radius: 20px;
          padding: 30px;
          box-shadow: 0 15px 35px rgba(0, 0, 0, 0.1);
          border: 1px solid rgba(13, 110, 253, 0.1);
        }
        
        .logo-orbital-ring {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 200px;
          height: 200px;
          border: 2px dashed rgba(13, 110, 253, 0.3);
          border-radius: 50%;
          animation: spin 20s linear infinite;
        }
        
        .logo-glow-effect {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 180px;
          height: 180px;
          background: radial-gradient(circle, rgba(13, 110, 253, 0.15) 0%, transparent 70%);
          border-radius: 50%;
          animation: pulse 4s infinite alternate;
        }
        
        .logo-image {
          max-height: 200px;
          width: auto;
          position: relative;
          z-index: 2;
          filter: drop-shadow(0 10px 20px rgba(0, 0, 0, 0.15));
          transition: transform 0.5s ease;
        }
        
        .animate-logo .logo-image {
          animation: bounceIn 1s ease-out;
        }
        
        /* Header Content - Right Side */
        .header-content {
          position: relative;
          padding-left: 30px;
        }
        
        .badge-wrapper {
          display: inline-block;
        }
        
        .history-badge {
          background: linear-gradient(45deg, #0d6efd, #198754);
          color: white;
          padding: 8px 20px;
          border-radius: 50px;
          font-size: 0.9rem;
          font-weight: 600;
          display: inline-flex;
          align-items: center;
          box-shadow: 0 5px 15px rgba(13, 110, 253, 0.3);
          animation: badgeFloat 3s infinite ease-in-out;
        }
        
        /* Main Title */
        .main-title {
          font-size: 3.5rem;
          font-weight: 800;
          margin-bottom: 1.5rem;
          position: relative;
          display: inline-block;
        }
        
        .title-text {
          color: #2c3e50;
          position: relative;
          animation: textReveal 1s ease-out;
        }
        
        .title-highlight {
          background: linear-gradient(45deg, #0d6efd, #198754);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          animation: gradientShift 3s infinite alternate;
          margin-left: 10px;
        }
        
        /* Description Box */
        .description-box {
          background: linear-gradient(135deg, #f8f9fa, #ffffff);
          border-radius: 15px;
          padding: 25px;
          margin: 30px 0;
          position: relative;
          border: 1px solid rgba(13, 110, 253, 0.1);
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
        }
        
        .description-icon {
          position: absolute;
          top: -15px;
          left: 30px;
          background: linear-gradient(45deg, #0d6efd, #198754);
          width: 40px;
          height: 40px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 1.2rem;
          box-shadow: 0 5px 15px rgba(13, 110, 253, 0.3);
        }
        
        .description-text {
          font-size: 1.1rem;
          line-height: 1.8;
          color: #34495e;
          margin-bottom: 20px;
          text-align: justify;
        }
        
        .description-footer {
          border-top: 1px solid rgba(13, 110, 253, 0.1);
          padding-top: 15px;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        
        .est-year {
          color: #0d6efd;
          font-size: 0.95rem;
        }
        
        /* Timeline Indicator */
        .timeline-indicator {
          margin-top: 30px;
        }
        
        .indicator-track {
          display: flex;
          align-items: center;
          justify-content: space-between;
          position: relative;
          padding: 0 20px;
        }
        
        .track-line {
          position: absolute;
          top: 50%;
          left: 0;
          right: 0;
          height: 4px;
          background: linear-gradient(90deg, 
            rgba(13, 110, 253, 0.2) 0%, 
            rgba(13, 110, 253, 0.8) 50%, 
            rgba(13, 110, 253, 0.2) 100%);
          z-index: 1;
        }
        
        .track-marker {
          position: relative;
          z-index: 2;
          text-align: center;
          animation: markerPulse 2s infinite;
        }
        
        .track-marker i {
          background: linear-gradient(45deg, #0d6efd, #198754);
          width: 50px;
          height: 50px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 1.2rem;
          margin: 0 auto 10px;
          box-shadow: 0 5px 15px rgba(13, 110, 253, 0.3);
        }
        
        .marker-label {
          font-size: 0.9rem;
          font-weight: 600;
          color: #2c3e50;
          display: block;
        }
        
        /* Card Animations */
        .history-card-wrapper {
          perspective: 1000px;
        }
        
        .animated-card {
          border: none;
          border-radius: 20px;
          background: linear-gradient(135deg, #ffffff, #f8f9fa);
          box-shadow: 0 25px 50px rgba(0, 0, 0, 0.1);
          transform-style: preserve-3d;
          transition: transform 0.6s ease;
          overflow: hidden;
          position: relative;
        }
        
        .animated-card:hover {
          transform: translateY(-10px);
          box-shadow: 0 35px 70px rgba(0, 0, 0, 0.15);
        }
        
        .slide-in {
          animation: slideInFromBottom 0.8s ease-out;
        }
        
        /* Card Header Decorative */
        .card-header-decorative {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 100px;
          overflow: hidden;
          z-index: 1;
        }
        
        .decorative-wave svg {
          width: 100%;
          height: 100%;
        }
        
        /* Section Title */
        .section-title-wrapper {
          text-align: center;
          margin-bottom: 50px;
          position: relative;
          z-index: 2;
        }
        
        .section-title {
          font-size: 2.5rem;
          font-weight: 700;
          color: #2c3e50;
          margin-bottom: 15px;
          animation: titleFloat 3s infinite ease-in-out;
        }
        
        .section-subtitle {
          color: #7f8c8d;
          font-size: 1.1rem;
          margin-bottom: 20px;
        }
        
        .title-divider {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 20px;
        }
        
        .divider-line {
          width: 80px;
          height: 3px;
          background: linear-gradient(90deg, transparent, #0d6efd, transparent);
        }
        
        .divider-icon {
          color: #0d6efd;
          font-size: 1.2rem;
          animation: spin 4s linear infinite;
        }
        
        /* Paragraph Layout */
        .history-paragraph {
          margin-bottom: 40px;
          display: flex;
          opacity: 0;
          position: relative;
        }
        
        .paragraph-enter {
          animation: fadeInUp 0.6s ease forwards;
        }
        
        /* Timeline Node */
        .timeline-node {
          flex-shrink: 0;
          margin-right: 30px;
          position: relative;
          width: 80px;
          display: flex;
          flex-direction: column;
          align-items: center;
        }
        
        .node-circle {
          width: 50px;
          height: 50px;
          background: linear-gradient(45deg, #0d6efd, #198754);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-weight: bold;
          font-size: 1.2rem;
          z-index: 2;
          box-shadow: 0 5px 20px rgba(13, 110, 253, 0.3);
          animation: circlePulse 2s infinite;
        }
        
        .node-connector {
          position: absolute;
          top: 50px;
          left: 50%;
          transform: translateX(-50%);
          width: 2px;
          height: calc(100% - 50px);
          background: linear-gradient(180deg, #0d6efd, rgba(13, 110, 253, 0.3));
        }
        
        .history-paragraph:last-child .node-connector {
          display: none;
        }
        
        /* Paragraph Content */
        .paragraph-content {
          flex: 1;
        }
        
        .paragraph-card {
          background: white;
          border-radius: 15px;
          overflow: hidden;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
          border: 1px solid rgba(13, 110, 253, 0.1);
          transition: transform 0.3s ease;
        }
        
        .paragraph-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 15px 40px rgba(0, 0, 0, 0.12);
        }
        
        .card-header {
          background: linear-gradient(135deg, #f8f9fa, #ffffff);
          padding: 20px;
          border-bottom: 1px solid rgba(13, 110, 253, 0.1);
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        
        .paragraph-title {
          font-size: 1.3rem;
          font-weight: 600;
          color: #2c3e50;
          margin: 0;
        }
        
        .chapter-badge {
          background: rgba(13, 110, 253, 0.1);
          color: #0d6efd;
          padding: 5px 15px;
          border-radius: 20px;
          font-size: 0.85rem;
          font-weight: 500;
        }
        
        .card-body {
          padding: 25px;
        }
        
        .drop-cap {
          font-size: 5rem;
          font-weight: 800;
          float: left;
          line-height: 0.8;
          margin-right: 15px;
          margin-top: 8px;
          background: linear-gradient(45deg, #0d6efd, #198754);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          animation: gradientShift 3s infinite alternate;
        }
        
        .paragraph-text {
          font-size: 1.05rem;
          line-height: 1.9;
          color: #34495e;
          text-align: justify;
        }
        
        .card-footer {
          background: #f8f9fa;
          padding: 15px 25px;
          border-top: 1px solid rgba(13, 110, 253, 0.1);
        }
        
        .footer-icons {
          display: flex;
          gap: 20px;
        }
        
        .icon-item {
          display: flex;
          align-items: center;
          gap: 8px;
          color: #7f8c8d;
          font-size: 0.9rem;
        }
        
        .icon-item i {
          color: #0d6efd;
        }
        
        /* Footer Statistics */
        .history-footer {
          background: linear-gradient(135deg, #f8f9fa, #ffffff);
          border-top: 1px solid rgba(0, 0, 0, 0.05);
          padding: 30px;
        }
        
        .footer-stats {
          display: flex;
          justify-content: space-around;
          flex-wrap: wrap;
          gap: 20px;
        }
        
        .stat-item {
          display: flex;
          align-items: center;
          gap: 15px;
          text-align: left;
          flex: 1;
          min-width: 200px;
        }
        
        .stat-icon {
          width: 60px;
          height: 60px;
          background: linear-gradient(45deg, #0d6efd, #198754);
          border-radius: 15px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 1.5rem;
          box-shadow: 0 5px 15px rgba(13, 110, 253, 0.3);
          animation: statIconFloat 3s infinite ease-in-out;
        }
        
        .stat-content h4 {
          font-size: 1.1rem;
          font-weight: 600;
          color: #2c3e50;
          margin: 0 0 5px 0;
        }
        
        .stat-content p {
          color: #7f8c8d;
          font-size: 0.9rem;
          margin: 0;
        }
        
        /* Floating Elements */
        .floating-elements {
          position: absolute;
          width: 100%;
          height: 100%;
          top: 0;
          left: 0;
          pointer-events: none;
          z-index: -1;
        }
        
        .floating-element {
          position: absolute;
          color: rgba(13, 110, 253, 0.08);
          font-size: 3rem;
          animation: floatElement 20s infinite linear;
        }
        
        .element-1 {
          top: 20%;
          left: 5%;
          animation-delay: 0s;
        }
        
        .element-2 {
          top: 60%;
          right: 10%;
          animation-delay: 5s;
        }
        
        .element-3 {
          bottom: 30%;
          left: 15%;
          animation-delay: 10s;
        }
        
        /* Helper Functions */
        .getParagraphTitle(index) {
          const titles = [
            'Founding Years',
            'Early Development',
            'Growth Phase',
            'Modern Era',
            'Present Day'
          ];
          return titles[index] || 'Historical Chapter';
        }
        
        .getChapterPeriod(index) {
          const periods = [
            '1990-2000',
            '2000-2010',
            '2010-2015',
            '2015-2020',
            '2020-Present'
          ];
          return periods[index] || 'Ongoing';
        }
        
        /* Animation Keyframes */
        @keyframes spin {
          from { transform: translate(-50%, -50%) rotate(0deg); }
          to { transform: translate(-50%, -50%) rotate(360deg); }
        }
        
        @keyframes pulse {
          0% { opacity: 0.3; transform: translate(-50%, -50%) scale(0.8); }
          100% { opacity: 0.6; transform: translate(-50%, -50%) scale(1.2); }
        }
        
        @keyframes bounceIn {
          0% { transform: scale(0.3); opacity: 0; }
          50% { transform: scale(1.1); }
          100% { transform: scale(1); opacity: 1; }
        }
        
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(30px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        @keyframes textReveal {
          from { clip-path: inset(0 100% 0 0); }
          to { clip-path: inset(0 0 0 0); }
        }
        
        @keyframes gradientShift {
          0% { filter: hue-rotate(0deg); }
          100% { filter: hue-rotate(360deg); }
        }
        
        @keyframes badgeFloat {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-5px); }
        }
        
        @keyframes markerPulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.1); }
        }
        
        @keyframes slideInFromBottom {
          from { transform: translateY(50px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        
        @keyframes titleFloat {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }
        
        @keyframes circlePulse {
          0%, 100% { transform: scale(1); box-shadow: 0 5px 20px rgba(13, 110, 253, 0.3); }
          50% { transform: scale(1.1); box-shadow: 0 8px 25px rgba(13, 110, 253, 0.4); }
        }
        
        @keyframes statIconFloat {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-8px); }
        }
        
        @keyframes float {
          0%, 100% { transform: translateY(0) rotate(0deg); }
          50% { transform: translateY(-20px) rotate(180deg); }
        }
        
        @keyframes floatElement {
          0% { transform: translateY(0) rotate(0deg); }
          25% { transform: translateY(-20px) rotate(90deg); }
          50% { transform: translateY(0) rotate(180deg); }
          75% { transform: translateY(20px) rotate(270deg); }
          100% { transform: translateY(0) rotate(360deg); }
        }
        
        @keyframes rotate {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        
        @keyframes colorShift {
          0% { filter: hue-rotate(0deg); }
          100% { filter: hue-rotate(360deg); }
        }
        
        /* Responsive Design */
        @media (max-width: 768px) {
          div[style*="height"] {
            height: 90px !important;
          }
          
          .main-title {
            font-size: 2.2rem;
          }
          
          .logo-wrapper {
            padding: 20px;
          }
          
          .logo-image {
            max-height: 150px;
          }
          
          .logo-orbital-ring {
            width: 160px;
            height: 160px;
          }
          
          .history-paragraph {
            flex-direction: column;
          }
          
          .timeline-node {
            margin-right: 0;
            margin-bottom: 20px;
            width: 100%;
            flex-direction: row;
          }
          
          .node-connector {
            top: 50%;
            left: 25px;
            transform: translateY(-50%);
            width: calc(100% - 50px);
            height: 2px;
          }
          
          .history-paragraph:last-child .node-connector {
            display: block;
          }
          
          .paragraph-card {
            margin-left: 0;
          }
          
          .footer-stats {
            flex-direction: column;
          }
          
          .section-title {
            font-size: 2rem;
          }
        }
        
        @media (min-width: 992px) {
          div[style*="height"] {
            height: 0px !important;
          }
          
          .header-content {
            padding-left: 50px;
          }
        }
        
        /* Custom Scrollbar */
        ::-webkit-scrollbar {
          width: 10px;
        }
        
        ::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 10px;
        }
        
        ::-webkit-scrollbar-thumb {
          background: linear-gradient(45deg, #0d6efd, #198754);
          border-radius: 10px;
        }
        
        ::-webkit-scrollbar-thumb:hover {
          background: linear-gradient(45deg, #0b5ed7, #157347);
        }
      `}</style>
    </>
  );
};

// Helper functions for paragraph titles and periods
const getParagraphTitle = (index) => {
  const titles = [
    'Founding Years',
    'Early Development',
    'Growth Phase',
    'Modern Era',
    'Present Day'
  ];
  return titles[index] || 'Historical Chapter';
};

const getChapterPeriod = (index) => {
  const periods = [
    '1990-2000',
    '2000-2010',
    '2010-2015',
    '2015-2020',
    '2020-Present'
  ];
  return periods[index] || 'Ongoing';
};

export default History;